import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
} from '@airbus/components-react';
import { useLanguageContextAPI } from '../../context/LanguageContext';
import styles from './ConfirmationModal.module.css';

export type ConfirmationModalProps = {
  openConfirmationModal?: boolean;
  setOpenConfirmationModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setCommitConfirmationProps: React.Dispatch<React.SetStateAction<any[]>>;
  shouldOpenConfirmation: boolean;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  title?: string; // i18n key
  confirmLabel?: string; // i18n key
  cancelLabel?: string; // i18n key
  children?: React.ReactNode | React.ReactNode[];
  mbdDataGrid?: any;
  beforeCommit?: () => Promise<void>;
  commitConfirmationProps?: CommitConfirmationProps | CommitConfirmationProps[];
};

type CommitConfirmationProps = {
  title?: string; // i18n key
  confirmLabel?: string; // i18n key
  cancelLabel?: string; // i18n key
  children?: React.ReactNode;
};

export default function ArrayConfirmationModal(arrProps: ConfirmationModalProps) {
  // expecting array | object
  const {
    openConfirmationModal,
    setOpenConfirmationModal,
    shouldOpenConfirmation,
    mbdDataGrid,
    onConfirm,
    onCancel,
    beforeCommit,
    setCommitConfirmationProps,
    commitConfirmationProps,
  } = arrProps;

  const { t } = useLanguageContextAPI();

  if (commitConfirmationProps && Array.isArray(commitConfirmationProps)) {
    const mergedCommitConfirmationProps = commitConfirmationProps?.reduce(
      (acc, curr) => {
        if (curr?.children !== undefined)
          acc.children.push(
            <>
              <p className={styles.title}>{t(`${curr.title}`)}</p>
              <p className={styles.indent}>{curr.children}</p>
            </>,
          );
        return acc;
      },
      {
        title: 'npiSteering.warning',
        confirmLabel: 'save_anyway',
        cancelLabel: 'cancel',
        children: [] as any[],
      },
    );
    return (
      <ConfirmationModal
        {...{
          openConfirmationModal,
          setOpenConfirmationModal,
          shouldOpenConfirmation,
          mbdDataGrid,
          onConfirm,
          onCancel,
          beforeCommit,
          setCommitConfirmationProps,
          ...(mergedCommitConfirmationProps ?? {}),
        }}
      />
    );
  } else if (commitConfirmationProps) {
    return (
      <ConfirmationModal
        {...{
          openConfirmationModal,
          setOpenConfirmationModal,
          shouldOpenConfirmation,
          mbdDataGrid,
          onConfirm,
          onCancel,
          beforeCommit,
          setCommitConfirmationProps,
          ...(commitConfirmationProps ?? {}),
        }}
      />
    );
  }
}

function ConfirmationModal(props: ConfirmationModalProps) {
  const {
    openConfirmationModal,
    onConfirm,
    onCancel,
    title = 'do_you_want_save',
    confirmLabel = 'ok',
    cancelLabel = 'cancel',
    children,
    mbdDataGrid,
    beforeCommit,
  } = props;

  const { t } = useLanguageContextAPI();

  const handleSave = async () => {
    try {
      if (beforeCommit) {
        await beforeCommit();
      }
    } catch (e) {
      console.error('ConfirmationModal: beforeCommit failed', e);
      return;
    }
    await onConfirm();
  };

  return (
    <Dialog
      scroll={Array.isArray(children) ? 'content' : 'dialog'}
      open={openConfirmationModal}
      onClose={onCancel}
      aria-describedby='alert-dialog-save'
    >
      <DialogHeader style={{ marginRight: '20px' }}>{t(title)}</DialogHeader>
      <DialogContent>
        {Array.isArray(children)
          ? children.map((child) => {
              return (
                <>
                  <div className={styles.outerBoxStyle}>{child}</div>
                </>
              );
            })
          : children}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          variant='secondary'
          data-testid={`${mbdDataGrid?.dataGridSource ?? 'grid'}_saveConfirm_btn_cancel`}
        >
          {t(cancelLabel)}
        </Button>
        <Button
          onClick={handleSave}
          variant='primary'
          data-testid={`${mbdDataGrid?.dataGridSource ?? 'grid'}_saveConfirm_btn_ok`}
        >
          {t(confirmLabel)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
